import { Settings01 } from "@untitledui/icons";
import { useTranslation } from "react-i18next";
import {
  Button as AriaButton,
  DialogTrigger as AriaDialogTrigger,
  Popover as AriaPopover,
} from "react-aria-components";
import { Avatar } from "@/components/base/avatar/avatar";
import { NavAccountMenu } from "@/components/application/app-navigation/base-components/nav-account-card";
import { IconAction } from "@/components/app/IconAction";
import { LocaleSelect } from "@/components/app/LocaleSelect";
import { ThemeIconToggle } from "@/components/app/ThemeIconToggle";
import { NotificationBell } from "@/components/app/NotificationBell";
import { PerformanceModeButton } from "@/components/app/PerformanceModeButton";
import { useAccountMenu } from "@/components/app/accountMenu";

/**
 * AppHeader — horní utilitní lišta nad obsahem:
 *
 * ```
 * ⚙ nastavení │ 🔔 oznámení │ [ Čeština ▾ ] │ ☾ │ ● avatar ▾
 * ```
 *
 * Motiv je jedna kontextová ikona (☾/☀), ne segmentový přepínač — plnou volbu
 * včetně „Systém" nabízí menu účtu a Nastavení → Vzhled. Jazyk je rozbalovací
 * seznam, aby lišta nerostla s každým dalším jazykem.
 *
 * Vodorovné menu odkazů tu záměrně není — navigace zůstává v sidebaru, lišta
 * nese jen utility. Menu pod avatarem je `NavAccountMenu`, tedy tentýž komponent
 * jako v kartě účtu v patičce sidebaru; obě místa proto nabízejí totéž.
 *
 * Jen desktop (`hidden lg:flex`): na mobilu už `sidebar-simple` renderuje vlastní
 * `MobileNavigationHeader` s hamburgerem a druhá lišta by se s ním tloukla. Vše
 * z lišty je na mobilu dostupné přes kartu účtu v rozbaleném sidebaru.
 */
export function AppHeader() {
  const { t } = useTranslation();
  const { display, menuTop, menuItems, onSignOut } = useAccountMenu();

  return (
    <header className="sticky top-0 z-40 hidden border-b border-secondary bg-primary lg:flex">
      <div className="flex w-full items-center justify-end gap-2 px-8 py-3">
        <IconAction icon={Settings01} label={t("header.settings")} href="/settings" />
        <PerformanceModeButton />
        <NotificationBell />

        <div className="mx-1 h-6 w-px bg-border-secondary" aria-hidden="true" />

        <LocaleSelect fullWidth={false} className="py-1.5" />
        <ThemeIconToggle />

        {display && (
          <AriaDialogTrigger>
            <AriaButton
              aria-label={t("header.account")}
              className="ml-1 cursor-pointer rounded-full outline-focus-ring focus-visible:outline-2 focus-visible:outline-offset-2"
            >
              <Avatar size="sm" initials={display.initials} alt={display.title} />
            </AriaButton>
            <AriaPopover placement="bottom right" offset={8}>
              <NavAccountMenu menuTop={menuTop} menuItems={menuItems} onSignOut={onSignOut} />
            </AriaPopover>
          </AriaDialogTrigger>
        )}
      </div>
    </header>
  );
}
